// Module-scoped holder for an initial user message that should be auto-sent
// once the V2 chat session is ready. Writer (the live-chat entry page, which
// mounts briefly and then navigates away via the pre-chat flow) and reader
// (the persistently-mounted chat container) live in different parts of the
// tree, so a singleton outside React state is the simplest hand-off.

export interface PendingInitialMessage {
  message: string;
  // Stable id minted once per user gesture (chip tap / text send) by the
  // `liveChatStart` action handler and threaded through navigation state. Used
  // to guarantee a single gesture is auto-sent at most once even if the
  // holder is observed more than once for the same gesture.
  dispatchId?: string;
}

// A gesture stashed here is normally consumed within moments, but a user can
// abandon the flow before the chat session ever reaches ready (e.g. leaving
// a pre-chat questionnaire unanswered, or a failed start request). Without an
// expiry, that stash would sit until the *next* time the session happens to
// become ready for any reason and auto-send a message the user no longer has
// context for.
const STALE_AFTER_MS = 5 * 60 * 1000;

interface StoredPendingInitialMessage extends PendingInitialMessage {
  stashedAt: number;
}

let pendingInitialMessage: StoredPendingInitialMessage | undefined;

const dispatchedInitialMessageIds = new Set<string>();

// Notified whenever a message is stashed, so a reader that mounted (and
// subscribed to its own readiness signal) before this call can react
// immediately instead of waiting for that signal to fire again on its own —
// the chat session may already be ready by the time a message is stashed.
const listeners = new Set<() => void>();

export const subscribePendingInitialMessage = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setPendingInitialMessage = (
  message: string | undefined,
  dispatchId?: string,
) => {
  pendingInitialMessage = message
    ? { message, dispatchId, stashedAt: Date.now() }
    : undefined;
  listeners.forEach((listener) => listener());
};

export const consumePendingInitialMessage = ():
  | PendingInitialMessage
  | undefined => {
  const stored = pendingInitialMessage;
  pendingInitialMessage = undefined;
  if (!stored) return undefined;
  if (Date.now() - stored.stashedAt > STALE_AFTER_MS) return undefined;

  const { message, dispatchId } = stored;
  return { message, dispatchId };
};

/**
 * Records that the gesture identified by `dispatchId` has been auto-sent and
 * returns whether it had ALREADY been sent. Returns `false` (i.e. "go ahead
 * and send") for a missing id so the absence of an id never blocks a send.
 */
export const markInitialMessageDispatched = (dispatchId?: string): boolean => {
  if (!dispatchId) return false;
  if (dispatchedInitialMessageIds.has(dispatchId)) return true;
  dispatchedInitialMessageIds.add(dispatchId);
  return false;
};

/**
 * Clears any stashed pending initial message (and the dispatched-id history)
 * without returning it. Because this lives outside React state and survives
 * across the SPA's lifetime, a message stashed by one member could otherwise
 * auto-send for the next member on a shared session — call this on sign-out.
 */
export const clearPendingInitialMessage = () => {
  pendingInitialMessage = undefined;
  dispatchedInitialMessageIds.clear();
};
